import {
  SearchParameterManager,
  SearchParameterManagerProps,
  buildSearchParameterManager,
  loadContextActions,
  loadDictionaryFieldContextActions,
  loadConfigurationActions,
  SearchConfigurationOptions,
  SearchEngineConfiguration,
  Middleware
} from "@coveo/headless";
import { loadRecommendationActions, RecommendationEngineConfiguration, RecommendationEngineOptions } from "@coveo/headless/recommendation";
import { waitForAtomic, waitForRecsAtomic } from "./utils/atomic";
import { waitForAccessToken } from "./utils/access-token";
import { initializeDataLayer } from "./utils/datalayer";
import { setVersion } from "./utils/version";
import { getGlobalVarByReference, getApiRequestUrl, getCleanedQ } from "./utils/utils";
import { AtomicRecsInterface, RecsInitializationOptions } from "@coveo/atomic/dist/types/components/recommendations/atomic-recs-interface/atomic-recs-interface";

const organizationId: string = getGlobalVarByReference('coveoOrgID') || process.env.ORGANIZATION_ID!;
let accessToken: string = process.env.API_KEY!;

async function initStandaloneSearchBox(searchInterface: any) {
  await searchInterface.initialize({
    accessToken,
    organizationId,
    organizationEndpoints:
      await searchInterface.getOrganizationEndpoints(organizationId),

      preprocessRequest: (request: any, clientOrigin: any, metadata: any) => {
        /* Includes the search hub value in the payload to make sure the categories fits the current search hub */
        if (metadata && metadata.method === 'facetSearch' && clientOrigin === 'searchApiFetch') {
          const body = JSON.parse(request.body);
          if (body.q) {
            body.context.cleanedQuery = getCleanedQ(body.q)
          }
          const searchHub = searchInterface.searchHub
          body.searchContext = {searchHub};
          request.body = JSON.stringify(body);
        }
        return request;
      },
    renewAccessToken: () => {
      return fetch(getGlobalVarByReference('ajaxUrl'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: 'action=renew_token',
        })
        .then((response) => response.json())
        .then((parsedValue) => {
          initializeDataLayer(parsedValue.data.token);
          return parsedValue.data.token;
        })
        .catch(console.error);
    },
  });

  let context: any = {
    hei_site: getGlobalVarByReference('siteName') || process.env.SITE!,
    website: getGlobalVarByReference('siteName') || process.env.SITE!,
    language: searchInterface.language,
  };

  const engine = searchInterface.engine;
  const contextAction = loadContextActions(engine).setContext(context);
  engine.dispatch(contextAction);
}

async function initRecs(recsInterfaces : any) {
  await Promise.all(
    recsInterfaces.map(async (recommendationInterface : HTMLAtomicRecsInterfaceElement) => {
      const RecSearchConfigurationOptions : RecommendationEngineConfiguration  = {
        accessToken,
        organizationId,
        organizationEndpoints: await recommendationInterface.getOrganizationEndpoints(organizationId),
        preprocessSearchResponseMiddleware: async (coveoResponse: any) => {
          const skuList : string[] = [];
          coveoResponse.body.results.forEach((coveoResult: any) => {
            if (coveoResult.raw["ec_sku"])
              skuList.push(coveoResult.raw["ec_sku"] );
          });
  
          if (skuList.length > 0) {
            var myHeaders = new Headers();
            myHeaders.delete("Content-Type")
  
            let payloadObj : any = {
              "a": "pricing-availability",
              "website": getGlobalVarByReference('siteName'),
              "email_address": getGlobalVarByReference('userEmailAddress') || "",
              "sku": skuList.join(',')
            }
  
            if ( localStorage.getItem("PS_API_SESSION_ID") != null) {
              payloadObj["session_id"] = localStorage.getItem("PS_API_SESSION_ID");
            }          
  
            const request = new Request(getApiRequestUrl(), {
              method: "POST",
              headers: myHeaders,
              body: JSON.stringify(payloadObj)
            });
                   
            const apiResponse = fetch(request)
              .then(resp => resp.text())
              .then(postResp => {
                if (JSON.parse(postResp).response.session_id) {
                  localStorage.setItem("PS_API_SESSION_ID", JSON.parse(postResp).response.session_id)           
                }              
                return postResp;
              })
              .catch((error) => {
                console.log('error', error)
              });
  
              const tempApiresponse = await coveoResponse;
              tempApiresponse.body.results.forEach((postApiCoveoResult: any) => {
                postApiCoveoResult.apiResponseData = apiResponse 
                return postApiCoveoResult;
              });
              return tempApiresponse
          }
  
          return coveoResponse;
        },

        preprocessRequest: (request: any, clientOrigin: any, metadata: any) => {
          if (metadata?.method === 'recommendations' && clientOrigin === 'searchApiFetch') {
            if (getGlobalVarByReference('coveoRecsContext')?.[recommendationInterface.id]?.skus) {
              let body = JSON.parse(request.body);
              if (body.q) {
                body.context.cleanedQuery = getCleanedQ(body.q)
              }
              body.mlParameters = {
                itemId: getGlobalVarByReference('coveoRecsContext')[recommendationInterface.id].skus[0],
                itemIds: getGlobalVarByReference('coveoRecsContext')[recommendationInterface.id].skus,
                brandFilter: getGlobalVarByReference('coveoRecsContext')[recommendationInterface.id].brand,
              }
              request.body = JSON.stringify(body);
            }
          }
            
          return request;
        },
        analytics: {
          analyticsClientMiddleware: (eventType: any, payload: any) => {
            if (eventType === 'click' && recommendationInterface.engine) {
              const matchingResult = recommendationInterface.engine.state.recommendation?.recommendations[payload.documentPosition - 1];
              payload = {
                ...payload,
                customData: {
                  ...payload.customData,
                  name: matchingResult.raw.ec_name || '',
                  category: matchingResult.raw.ec_category || [],
                  brand: matchingResult.raw.ec_brand || '',
                  price: matchingResult.raw.ec_price || 0,
                }
              };
            }
            return payload;
          },
        },
        renewAccessToken: () => {
          return fetch(getGlobalVarByReference('ajaxUrl'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: 'action=renew_token',
            })
            .then((response) => response.json())
            .then((parsedValue) => {
              initializeDataLayer(parsedValue.data.token);
              return parsedValue.data.token;
            })
            .catch(console.error);
        },
      };

      recommendationInterface.initialize(RecSearchConfigurationOptions)
    }
    )
  );

  recsInterfaces.forEach((recInterface : any) => {
    let context: any = {
      hei_site: getGlobalVarByReference('siteName'),
      website: getGlobalVarByReference('siteName'),
      language: recInterface.language,
    };
    const engine = recInterface.engine;
    const contextAction = loadContextActions(engine).setContext(context);
    engine.dispatch(contextAction);

    if (recInterface.id) {
      const recommenderIdAction = loadRecommendationActions(engine).setRecommendationId({id: recInterface.id});
      engine.dispatch(recommenderIdAction);
    }
    recInterface.getRecommendations();
  });
}

async function initSearch(searchInterface: any) {
  await searchInterface.initialize({
    accessToken,
    organizationId,
    organizationEndpoints:
      await searchInterface.getOrganizationEndpoints(organizationId),
    preprocessRequest: (request: any, clientOrigin: any, metadata: any) => {
      if (metadata?.method === 'search' && clientOrigin === 'searchApiFetch') {
        const body = JSON.parse(request.body);
        if (body.q) {
          body.context.cleanedQuery = getCleanedQ(body.q)
        }
        // Override generated facet request count to bypass headless limit of 10
        body.generateAutomaticFacets ? body.generateAutomaticFacets.desiredCount = 20 : body.generateAutomaticFacets = {desiredCount : 20}
        
        delete body.fieldsToInclude;
        if(body.tab != "documents"){
          const facet = body.facets?.filter((facet: any) => {
            return facet.facetId === 'categories';
          });
          let selectedValue = facet?.[0].currentValues[0];
          if (selectedValue) {
            while (selectedValue.children.length && selectedValue?.state !== 'selected') {
              selectedValue = selectedValue.children[0];
            }
            selectedValue.retrieveCount = 10001;
          }
          body.groupBy = [
          {
            field: "@ec_category",
            maximumNumberOfValues: 10001,
            sortCriteria: "alphaDescending",
            injectionDepth: 1000000,
            completeFacetWithStandardValues: true,
            allowedValues: []
          },{
            field: "@ec_category",
            maximumNumberOfValues: 50,
            sortCriteria: "score",
            injectionDepth: 1000000,
            completeFacetWithStandardValues: true,
            allowedValues: []
          },
          {
            field: "@objecttype",
            queryOverride: "$q()",
            injectionDepth: 1000000,
            disjunctionQueryOverride : "$q()"
          }];
        }
        else {
          // Because the disable action does not disable the categorical facet, it is removed here. 

          body.facets = body.facets.filter((facet: any) => facet.facetId !== "ec_category" )
          body.facets = body.facets.filter((facet: any) => facet.facetId !== "categories" )

          body.groupBy = [{
            field: "@objecttype",
            queryOverride: "$q()",
            injectionDepth: 1000000,
            disjunctionQueryOverride : "$q()"
          }]
        }


        if (body.context.category) {
          body.tab = body.context.category;
        }
        request.body = JSON.stringify(body);
        try {
          sessionStorage.setItem("requestHeaders", JSON.stringify(request.headers));
          sessionStorage.setItem("requestBody", request.body);
          sessionStorage.setItem("url", request.url);
        } catch(e){
          console.error(e);
        }

        
      }
      /* Includes the search hub value in the payload to make sure the categories fits the current search hub */
      if (metadata && metadata.method === 'facetSearch' && clientOrigin === 'searchApiFetch') {
        const body = JSON.parse(request.body);
        if (body.q) {
          body.context.cleanedQuery = getCleanedQ(body.q)
        }
        const searchHub = searchInterface.searchHub
        body.searchContext = {searchHub};
        request.body = JSON.stringify(body);
      }
      return request;
    },
    search: { 
      preprocessSearchResponseMiddleware: async (coveoResponse: any) => {
        const skuList : string[] = [];
        coveoResponse.body.results.forEach((coveoResult: any) => {
          if (coveoResult.raw["ec_sku"])
            skuList.push(coveoResult.raw["ec_sku"] );
        });

        if (skuList.length > 0) {
          var myHeaders = new Headers();
          myHeaders.delete("Content-Type")

          let payloadObj : any = {
            "a": "pricing-availability",
            "website": getGlobalVarByReference('siteName'),
            "email_address": getGlobalVarByReference('userEmailAddress') || "",
            "sku": skuList.join(',')
          }

          if ( localStorage.getItem("PS_API_SESSION_ID") != null) {
            payloadObj["session_id"] = localStorage.getItem("PS_API_SESSION_ID");
          }          

          const request = new Request(getApiRequestUrl(), {
            method: "POST",
            headers: myHeaders,
            body: JSON.stringify(payloadObj)
          });
                 
          const apiResponse = fetch(request)
            .then(resp => resp.text())
            .then(postResp => {
              if (JSON.parse(postResp).response.session_id) {
                localStorage.setItem("PS_API_SESSION_ID", JSON.parse(postResp).response.session_id)           
              }              
              return postResp;
            })
            .catch((error) => {
              console.log('error', error)
            });

            const tempApiresponse = await coveoResponse;
            tempApiresponse.body.results.forEach((postApiCoveoResult: any) => {
              postApiCoveoResult.apiResponseData = apiResponse 
              return postApiCoveoResult;
            });
            return tempApiresponse
        }

        return coveoResponse;
      },
    },
    analytics: {
      analyticsClientMiddleware: (eventType: any, payload: any) => {
        if (eventType === 'click' && payload.originLevel2 === 'products') {
          const matchingResult = searchInterface.engine.state.search?.results[payload.documentPosition - 1];
          payload = {
            ...payload,
            customData: {
              ...payload.customData,
              name: matchingResult.raw.ec_name || '',
              category: matchingResult.raw.ec_category || [],
              brand: matchingResult.raw.ec_brand || '',
              price: matchingResult.raw.ec_price || 0,
            }
          };
        }
        return payload;
      },
    },
    renewAccessToken: () => {
      return fetch(getGlobalVarByReference('ajaxUrl'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: 'action=renew_token',
        })
        .then((response) => response.json())
        .then((parsedValue) => {
          initializeDataLayer(parsedValue.data.token);
          return parsedValue.data.token;
        })
        .catch(console.error);
    },
  });

  const engine = searchInterface.engine;

  let context: any = {
    hei_site: getGlobalVarByReference('siteName'),
    website: getGlobalVarByReference('siteName'),
    language: searchInterface.language,
  };
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has('hei_mcampaign')) {
    context.mcampaign = searchParams.get('hei_mcampaign');
  }
  if (getGlobalVarByReference('coveoCategory')) {
    context.category = getGlobalVarByReference('coveoCategory').reduce((acc: string, cur: any) => acc += '|' + cur.category_name, '').slice(1);
    const configurationAction = loadConfigurationActions(engine).setOriginLevel2({
      originLevel2: context.category,
    });
    engine.dispatch(configurationAction);
  }

  const dictionaryContext: any = {
    hei_in_stock: getGlobalVarByReference('siteName') || process.env.SITE!,
    hei_in_stock_all: getGlobalVarByReference('siteName') || process.env.SITE!
  };

  const contextAction = loadContextActions(engine).setContext(context);
  engine.dispatch(contextAction);
  const dictionaryContextAction = loadDictionaryFieldContextActions(engine).setContext(dictionaryContext);
  engine.dispatch(dictionaryContextAction);
  
  const jsonCfParams = localStorage.getItem("PS_TEMP_CF_PARAMS")
  if (jsonCfParams != null ) {
    const cfParams = JSON.parse(jsonCfParams)
    const managerProp : SearchParameterManagerProps = {
      initialState: {
        parameters: {
          cf: cfParams
        }
      }
    }
    localStorage.removeItem("PS_TEMP_CF_PARAMS")
    buildSearchParameterManager(engine, managerProp)
  }

  searchInterface.i18n.addResourceBundle('en', 'caption-hei_rohs_compliance', {
    Y: 'Yes',
    N: 'No',
    U: "Unknown"
  });
  searchInterface.i18n.addResourceBundle('en', 'caption-hei_in_stock_all', {
    True: 'Yes',
    All: 'All'
  });

  searchInterface.executeFirstSearch();
}

async function main() {
  try {
    accessToken = await waitForAccessToken();
    initializeDataLayer(accessToken);
  } catch (e) {
    console.error(e);
  }

  await waitForAtomic();
  const ssbSearchInterface: any = document.querySelector("atomic-search-interface#atomic-ssb");
  ssbSearchInterface && initStandaloneSearchBox(ssbSearchInterface);

  const searchInterface: any = document.querySelector("atomic-search-interface#atomic-search") || document.querySelector("atomic-search-interface#atomic-plp");
  searchInterface && initSearch(searchInterface);

  await waitForRecsAtomic();
  const recsInterfaces: HTMLAtomicRecsInterfaceElement[] = Array.from(document.querySelectorAll('atomic-recs-interface'));
  recsInterfaces.length && initRecs(recsInterfaces);
}

main();
setVersion();
